import { CSSProperties, useId, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

/** CLE-2884 — Ani: "when content is too long collapse it after 15 lines". */
export const DEFAULT_COLLAPSED_LINES = 15;

/**
 * Whether `text` has more than `maxLines` lines by its own line breaks. A floor for the real
 * check, which measures the rendered box: every line break starts a visual line, so this never
 * claims an overflow the browser would not show — it only misses lines that wrap. It is what
 * decides in jsdom (no layout) and before the first measurement. Trailing blank lines do not
 * count, because pre-wrap does not render a final line break as a line. Only LF and CRLF break
 * lines: pre-wrap renders a lone CR in DOM text as a space.
 */
export const exceedsLineLimit = (text: string, maxLines: number): boolean =>
  text.replace(/\s+$/, "").split(/\r\n|\n/).length > maxLines;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-purple);
  font: inherit;
  font-size: 0.875rem;
  cursor: pointer;
`;

export interface ICollapsibleTextProps {
  text: string;
  /** Visual lines shown while collapsed. */
  maxLines?: number;
  className?: string;
}

/**
 * Long free text, clamped to `maxLines` lines with a Show More / Show Less toggle. Line breaks
 * the user typed are kept (`white-space: pre-wrap`). The toggle appears only when the text is
 * actually cut: measured after layout (and again on resize), with `exceedsLineLimit` as the
 * fallback where nothing can be measured.
 */
export function CollapsibleText({
  text,
  maxLines = DEFAULT_COLLAPSED_LINES,
  className = "text-900",
}: ICollapsibleTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const textId = useId();
  const [expanded, setExpanded] = useState(false);
  const [measuredOverflow, setMeasuredOverflow] = useState(false);

  // New text starts collapsed again — adjusted during render, so it never paints open first.
  const [prevText, setPrevText] = useState(text);
  if (text !== prevText) {
    setPrevText(text);
    setExpanded(false);
  }

  // Measured only while clamped: expanded, nothing is cut, so the last measurement stands.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return undefined;

    const measure = () =>
      setMeasuredOverflow(el.scrollHeight > el.clientHeight + 1);
    measure();

    // jsdom has no ResizeObserver; the heuristic covers it there.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, maxLines, expanded]);

  const overflows = measuredOverflow || exceedsLineLimit(text, maxLines);

  const clampStyle: CSSProperties = expanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  return (
    <div>
      <div
        ref={textRef}
        id={textId}
        className={className}
        style={{ whiteSpace: "pre-wrap", ...clampStyle }}
      >
        {text}
      </div>
      {overflows && (
        <ToggleButton
          type="button"
          className="mt-2"
          aria-expanded={expanded}
          aria-controls={textId}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? (
            <>
              Show Less <i className="pi pi-angle-up ml-1" />
            </>
          ) : (
            <>
              Show More <i className="pi pi-angle-down ml-1" />
            </>
          )}
        </ToggleButton>
      )}
    </div>
  );
}
